import {
    Bundle,
    ClientAccount as LuneClientAccount,
    UpdateClientAccountRequest,
} from '@lune-climate/lune'
import { useErrorSnackbar } from '@lune-fe/lune-components-lib'
import { ButtonNew, LoadingWrapper, MainLayoutContainerNew } from '@lune-fe/lune-ui-lib'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import AccountFormWrapper, {
    AccountFormKeys,
    AccountFormType,
    BundleSettings,
    IAccountFormData,
} from 'components/AccountForm/AccountFormWrapper'
import OnlyAPIClientsTooltip from 'components/OnlyAPIClientsTooltip'
import { removeClientAccountLogo, uploadClientAccountLogo } from 'endpoints/api'
import { getClientAccountById } from 'endpoints/dapi'
import useBundlesPortfolios from 'hooks/useBundlesPortfolios'
import useIndividualAccountBundles from 'hooks/useIndividualAccountBundles'
import useIsTestMode from 'hooks/useIsTestMode'
import { useLuneClient } from 'hooks/useLuneClient'
import useTitle from 'hooks/useTitle'
import { useIsAuthorised } from 'hooks/useUserState'
import { queryKeys } from 'queryKeys'
import { SnackbarMessages } from 'SnackbarMessages'
import { CUSTOM_PORTFOLIO_ID } from 'views/Settings/BundlePicker/BundlePicker'

const ClientAccount = () => {
    const navigate = useNavigate()
    const isTestMode = useIsTestMode()
    const isAuthorised = useIsAuthorised()
    const luneClient = useLuneClient()
    const queryClient = useQueryClient()

    const [account, setAccount] = useState<LuneClientAccount>()
    const [bundleSelection, setBundleSelection] = useState<Record<string, number | string>>()
    const [bundles, setBundles] = useState<Bundle[]>()
    const { accountId } = useParams<{ accountId: string }>()
    const { loadBundles, loadAllBundles, updateCustomBundleSelection } =
        useIndividualAccountBundles()
    const { bundlesPortfolios, loading: loadingBundles } = useBundlesPortfolios()
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()

    const [formValue, setFormValue] = useState<IAccountFormData>()
    const [bundleSettings, setBundleSettings] = useState<BundleSettings>()
    const [bundlePickerError, setBundlePickerError] = useState<boolean>(false)
    const [submittingForm, setSubmittingForm] = useState<boolean>(false)
    const [logoUpdating, setLogoUpdating] = useState<boolean>(false)

    const latestRequestIdRef = useRef<string | null>(null)

    useTitle(account?.name)

    /**
     * Loads account data and handles race condition protection
     */
    const loadAccount = async (
        id: string,
        requestId: string,
        latestRequestIdRef: React.MutableRefObject<string | null>,
    ) => {
        const result = await getClientAccountById(id)

        if (latestRequestIdRef.current !== requestId) {
            return
        }

        if (result.isOk()) {
            const loadedAccount = result.value
            setAccount(loadedAccount)

            const bundlesResult = await loadBundles(loadedAccount.id)
            if (latestRequestIdRef.current !== requestId) {
                return
            }
            setBundleSelection(bundlesResult)

            const allBundlesResult = await loadAllBundles(loadedAccount.id)
            if (latestRequestIdRef.current !== requestId) {
                return
            }
            setBundles(allBundlesResult)
            setSubmittingForm(false)
        } else {
            snackbar(SnackbarMessages.GENERIC_ERROR, {
                variant: 'error',
                error: result.error,
            })
            navigate('/client-accounts')
        }
    }

    useEffect(() => {
        setAccount(undefined)
        setBundleSelection(undefined)
        setBundles(undefined)

        const requestId = `${accountId}-${Date.now()}`
        latestRequestIdRef.current = requestId

        void loadAccount(accountId!, requestId, latestRequestIdRef)

        return () => {
            if (latestRequestIdRef.current === requestId) {
                latestRequestIdRef.current = null
            }
        }
    }, [accountId, loadBundles, loadAllBundles, snackbar, navigate])

    const onUpdateSuccess = async () => {
        const requestId = `${accountId}-${Date.now()}`
        latestRequestIdRef.current = requestId

        loadAccount(accountId!, requestId, latestRequestIdRef)
        // Invalidate client accounts query to trigger refetch in components using useClientAccounts
        await queryClient.invalidateQueries(queryKeys.GET_CLIENT_ACCOUNTS)
        snackbar(SnackbarMessages.SETTINGS_SAVED)
    }

    const handleLogoUpdate = useCallback(
        async (file: File | null) => {
            if (!account) {
                return
            }
            setLogoUpdating(true)
            try {
                if (file) {
                    // Upload new logo
                    const formData = new FormData()
                    formData.append('logo', file)
                    const result = await uploadClientAccountLogo(account.id, formData)
                    if (result.isOk()) {
                        onUpdateSuccess()
                    } else {
                        snackbar(SnackbarMessages.GENERIC_ERROR, {
                            variant: 'error',
                            error: result.error,
                        })
                    }
                } else {
                    const result = await removeClientAccountLogo(account.id)
                    if (result.isOk()) {
                        onUpdateSuccess()
                    } else {
                        snackbar(SnackbarMessages.GENERIC_ERROR, {
                            variant: 'error',
                            error: result.error,
                        })
                    }
                }
            } catch (error) {
                console.error('Failed to update logo:', error)
                snackbar(SnackbarMessages.GENERIC_ERROR, {
                    variant: 'error',
                    error,
                })
            } finally {
                setLogoUpdating(false)
            }
        },
        [account, snackbar, onUpdateSuccess],
    )

    const onSubmit = async (data: IAccountFormData) => {
        if (!account) {
            return
        }

        await updateCustomBundleSelection(account.id, data.bundleSettings.customBundleSelection)
        const selectedPortfolioId = data.bundleSettings.selectedBundlePortfolioId
        return luneClient
            .updateClientAccount(account.id, {
                updateClientAccountRequest: {
                    name: data.name,
                    ...(data.beneficiary && { beneficiary: data.beneficiary }),
                    handle: data.handle,
                    externalPagesVisibility: data.externalPagesVisibility,
                    bundlePortfolioId:
                        selectedPortfolioId === CUSTOM_PORTFOLIO_ID
                            ? undefined
                            : selectedPortfolioId,
                } as UpdateClientAccountRequest & { bundlePortfolioId?: string },
            })
            .then((r) => r.unwrap())
            .catch((error) => {
                snackbar(SnackbarMessages.GENERIC_ERROR, {
                    variant: 'error',
                    error,
                })
            })
    }

    return (
        <LoadingWrapper
            loading={!account || !bundleSelection || loadingBundles}
            sx={{
                minHeight: '100vh',
            }}
        >
            {account && !loadingBundles && (
                <MainLayoutContainerNew
                    isTestMode={!!isTestMode}
                    title={account.name}
                    breadCrumbs={[
                        {
                            label: 'Client Accounts',
                            onClick: () => {
                                navigate(`/client-accounts`)
                            },
                        },
                        {
                            label: account.name,
                        },
                    ]}
                    primaryButton={
                        <ButtonNew
                            data-testid="account-submit-btn"
                            disabled={
                                !formValue?.name?.trim() ||
                                !formValue.currency ||
                                bundlePickerError ||
                                !isAuthorised ||
                                submittingForm
                            }
                            onClick={async () => {
                                if (formValue && bundleSettings) {
                                    setSubmittingForm(true)
                                    const result = await onSubmit({
                                        name: formValue.name,
                                        currency: formValue.currency,
                                        beneficiary: formValue.beneficiary?.trim() || undefined,
                                        handle: formValue.handle,
                                        externalPagesVisibility: formValue.externalPagesVisibility,
                                        bundleSettings,
                                    })
                                    if (result) {
                                        onUpdateSuccess()
                                    } else {
                                        setSubmittingForm(false)
                                    }
                                }
                            }}
                        >
                            Save changes
                        </ButtonNew>
                    }
                >
                    <AccountFormWrapper
                        /* @ts-expect-error: This read-only wrapper never invokes its form callbacks. */
                        onSubmit={() => {}}
                        onSuccess={() => {}}
                        initialValues={{
                            [AccountFormKeys.NAME]: account.name,
                            [AccountFormKeys.CURRENCY]: account.currency,
                            [AccountFormKeys.BENEFICIARY]: account.beneficiary ?? '',
                            [AccountFormKeys.HANDLE]: account.handle ?? undefined,
                            [AccountFormKeys.EXTERNAL_PAGES_VISIBILITY]:
                                account.externalPagesVisibility,
                            [AccountFormKeys.BUNDLE_SETTINGS]: {
                                customBundleSelection: bundleSelection!,
                                selectedBundlePortfolioId:
                                    account.bundlePortfolioId ?? CUSTOM_PORTFOLIO_ID,
                            },
                        }}
                        bundles={bundles}
                        forbiddenTooltip={OnlyAPIClientsTooltip}
                        bundlesPortfolios={bundlesPortfolios}
                        onFormValueChange={(formVal, bundleSettings, bundleError) => {
                            setFormValue(formVal)
                            setBundleSettings(bundleSettings)
                            setBundlePickerError(bundleError)
                        }}
                        formType={AccountFormType.CLIENT_ACCOUNT}
                        account={account}
                        existingAccount
                        readOnly={!isAuthorised}
                        onLogoUpdate={handleLogoUpdate}
                        logoUpdating={logoUpdating}
                    />
                </MainLayoutContainerNew>
            )}
        </LoadingWrapper>
    )
}

export default ClientAccount
